import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast } from "sonner";
import { Search, UserCheck, UserPlus, History, ShieldAlert, RefreshCw, XCircle } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Navigate } from "react-router-dom";

const METHODS = [
  { v: "enzona", l: "EnZona" },
  { v: "transfermovil", l: "Transfermóvil" },
  { v: "efectivo", l: "Efectivo" },
  { v: "otro", l: "Otro" },
];
const CURRENCIES = [
  { v: "cup", l: "CUP" },
  { v: "usd", l: "USD" },
  { v: "otra", l: "Otra" },
];

export default function AdminManualPayments({ embedded = false }) {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [lookup, setLookup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ payment_method: "enzona", amount: "", currency: "cup", reference_note: "" });
  const [history, setHistory] = useState([]);
  const [historyEmail, setHistoryEmail] = useState("");

  const loadHistory = async (q = "") => {
    try {
      const r = await api.get("/admin/manual/history" + (q ? `?email=${encodeURIComponent(q)}` : ""));
      setHistory(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadHistory(); }, []);

  // Server-side guard is the source of truth; this is UX-only
  if (!user?.is_admin) return <Navigate to="/panel" replace />;

  const doLookup = async () => {
    if (!email) return;
    setBusy(true); setLookup(null);
    try {
      const r = await api.get(`/admin/users/lookup?email=${encodeURIComponent(email)}`);
      setLookup(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo consultar.");
    } finally { setBusy(false); }
  };

  const doActivate = async () => {
    if (!form.amount) { toast.error("Introduce el importe"); return; }
    setBusy(true);
    try {
      await api.post("/admin/manual/activate", { email, ...form, amount: parseFloat(form.amount) });
      toast.success("Acceso premium activado correctamente.");
      await doLookup(); await loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo activar.");
    } finally { setBusy(false); }
  };

  const doInvite = async () => {
    if (!form.amount) { toast.error("Introduce el importe"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/manual/invite", { email, ...form, amount: parseFloat(form.amount) });
      toast.success("Invitación enviada.");
      if (r.data.invite_url_debug) {
        // Preview only — quick copy-paste helper for the admin
        navigator.clipboard?.writeText(r.data.invite_url_debug).catch(() => {});
        toast.info("Enlace copiado al portapapeles (preview).");
      }
      await loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar el correo. Inténtalo nuevamente.");
    } finally { setBusy(false); }
  };

  const doResend = async (mp) => {
    if (!window.confirm(`Reenviar invitación a ${mp.email}? El enlace anterior quedará invalidado.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/manual/${mp.id}/resend-invitation`);
      toast.success("Invitación reenviada correctamente.");
      if (r.data.invite_url_debug) {
        navigator.clipboard?.writeText(r.data.invite_url_debug).catch(() => {});
        toast.info("Enlace copiado al portapapeles (preview).");
      }
      await loadHistory(historyEmail);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar el correo. Inténtalo nuevamente.");
    } finally { setBusy(false); }
  };

  const doCancelInvite = async (mp) => {
    if (!window.confirm(`Cancelar la invitación pendiente para ${mp.email}? El pago manual queda intacto.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/manual/${mp.id}/cancel-invitation`);
      toast.success("Invitación cancelada. El pago se conserva intacto.");
      await loadHistory(historyEmail);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cancelar.");
    } finally { setBusy(false); }
  };

  const inner = (
      <div className="space-y-8 max-w-5xl" data-testid="admin-manual-page">

        {/* Lookup */}
        <section className="mep-card p-7">
          <div className="flex items-center gap-3 mb-4">
            <Search size={18} className="text-[var(--mep-pink)]" />
            <h2 className="font-display text-xl font-light tracking-tight">Buscar cliente por correo</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="admin-lookup-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="correo@ejemplo.com"
              className="flex-1 bg-[var(--mep-surface)] border border-[var(--mep-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--mep-pink)]"
            />
            <button data-testid="admin-lookup-btn" onClick={doLookup} disabled={busy || !email} className="btn-pink px-6 py-3 font-medium">
              {busy ? "Buscando..." : "Buscar"}
            </button>
          </div>
        </section>

        {/* Result */}
        {lookup && (
          <section className="mep-card p-7" data-testid="admin-lookup-result">
            {lookup.exists ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <UserCheck size={18} className="text-[var(--mep-gold)]" />
                  <h3 className="font-display text-xl font-light tracking-tight">Usuario encontrado</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 text-sm mb-6">
                  <Info label="Correo" value={lookup.email} />
                  <Info label="Nombre" value={lookup.name || "—"} />
                  <Info label="Estado" value={lookup.is_premium ? "Premium activo" : "Sin acceso premium"} tone={lookup.is_premium ? "gold" : "pink"} />
                  <Info label="Perfil" value={lookup.has_profile ? "Sí" : "No"} />
                </div>
                {lookup.is_premium ? (
                  <div className="p-4 rounded-lg bg-[rgba(212,175,55,0.10)] border-l-2 border-[var(--mep-gold)] text-sm" data-testid="admin-already-premium">
                    Este usuario ya tiene acceso premium.
                  </div>
                ) : (
                  <FormActivate form={form} setForm={setForm} onSubmit={doActivate} busy={busy} label="Confirmar activación" testid="admin-activate-btn" />
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <UserPlus size={18} className="text-[var(--mep-pink)]" />
                  <h3 className="font-display text-xl font-light tracking-tight">Este correo todavía no tiene una cuenta.</h3>
                </div>
                <p className="text-sm text-[var(--mep-text-muted)] mb-5">
                  Envía una invitación segura para que el cliente cree su propia contraseña. El enlace expira en 7 días.
                </p>
                <FormActivate form={form} setForm={setForm} onSubmit={doInvite} busy={busy} label="Enviar invitación para crear cuenta" testid="admin-invite-btn" />
              </>
            )}
          </section>
        )}

        {/* History */}
        <section className="mep-card p-7">
          <div className="flex items-center gap-3 mb-4 justify-between">
            <div className="flex items-center gap-3">
              <History size={18} className="text-[var(--mep-gold)]" />
              <h2 className="font-display text-xl font-light tracking-tight">Historial de activaciones manuales</h2>
            </div>
            <div className="flex gap-2">
              <input
                data-testid="admin-history-search"
                value={historyEmail}
                onChange={(e) => setHistoryEmail(e.target.value)}
                placeholder="Buscar email..."
                className="bg-[var(--mep-surface)] border border-[var(--mep-border)] rounded-lg px-3 py-1.5 text-sm w-48"
              />
              <button onClick={() => loadHistory(historyEmail)} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--mep-surface-2)] hover:bg-[var(--mep-surface)]">
                Buscar
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)]">
                <tr><th className="text-left py-2 pr-4">Fecha</th><th className="text-left pr-4">Email</th><th className="text-left pr-4">Método</th><th className="text-right pr-4">Importe</th><th className="pr-4">Moneda</th><th className="pr-4">Estado</th><th className="text-left pr-6 whitespace-nowrap">Activado por</th><th className="text-left pr-4 min-w-[200px]">Referencia / Nota</th><th className="text-right">Acciones</th></tr>
              </thead>
              <tbody data-testid="admin-history-tbody">
                {history.length === 0 && (<tr><td colSpan="9" className="py-6 text-center text-[var(--mep-text-muted)]">Sin registros aún.</td></tr>)}
                {history.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--mep-border)]">
                    <td className="py-2 pr-4 text-[var(--mep-text-muted)] whitespace-nowrap">{new Date(r.created_at).toLocaleString("es-ES")}</td>
                    <td className="pr-4">{r.email}</td>
                    <td className="pr-4 capitalize">{r.payment_method}</td>
                    <td className="pr-4 text-right">{r.amount}</td>
                    <td className="pr-4 uppercase text-center">{r.currency}</td>
                    <td className="pr-4 text-center"><span className={`text-xs uppercase tracking-wide ${r.status==="activated"?"text-[var(--mep-gold)]":r.status==="paid"?"text-[var(--mep-pink)]":"text-[var(--mep-text-muted)]"}`}>{r.status}</span></td>
                    <td className="pr-6 text-[var(--mep-text-muted)]" title={r.activated_by || ""}>{r.activated_by ? "Admin" : "—"}</td>
                    <td data-testid={`admin-history-note-${r.id}`} className="pr-4 text-[var(--mep-text-muted)] max-w-[260px] truncate" title={r.reference_note || ""}>{r.reference_note || "—"}</td>
                    <td className="text-right whitespace-nowrap">
                      {!r.has_account && (
                        <div className="inline-flex items-center gap-2">
                          <button
                            data-testid={`admin-resend-invite-${r.id}`}
                            onClick={() => doResend(r)}
                            disabled={busy}
                            title="Invalida el token anterior y envía uno nuevo. El pago no se toca."
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-[var(--mep-pink)]/40 text-[var(--mep-pink)] hover:bg-[rgba(232,61,132,0.08)] transition"
                          >
                            <RefreshCw size={12} /> Reenviar invitación
                          </button>
                          {r.has_pending_invitation && (
                            <button
                              data-testid={`admin-cancel-invite-${r.id}`}
                              onClick={() => doCancelInvite(r)}
                              disabled={busy}
                              title="Invalida la invitación pendiente. El pago manual queda intacto."
                              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-[var(--mep-border)] text-[var(--mep-text-muted)] hover:text-white hover:border-[var(--mep-gold)]/40 transition"
                            >
                              <XCircle size={12} /> Cancelar invitación
                            </button>
                          )}
                        </div>
                      )}
                      {r.has_account && (
                        <span className="text-xs text-[var(--mep-text-muted)]">Cuenta creada</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="text-xs text-[var(--mep-text-muted)] flex items-center gap-2 pt-4">
          <ShieldAlert size={14} /> Panel restringido. Sesión: {user?.email}
        </div>
      </div>
  );

  if (embedded) return inner;
  return (
    <AppShell title="Pagos manuales — Cuba" subtitle="Activación de acceso premium sin Stripe.">
      {inner}
    </AppShell>
  );
}

const Info = ({ label, value, tone }) => (
  <div>
    <div className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)]">{label}</div>
    <div className={`mt-1 ${tone === "gold" ? "text-[var(--mep-gold)]" : tone === "pink" ? "text-[var(--mep-pink)]" : "text-[var(--mep-text)]"}`}>{value}</div>
  </div>
);

const FormActivate = ({ form, setForm, onSubmit, busy, label, testid }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Método de pago</label>
      <select data-testid="admin-method-select" value={form.payment_method} onChange={(e)=>setForm({...form,payment_method:e.target.value})} className="w-full bg-[var(--mep-surface)] border border-[var(--mep-border)] rounded-lg px-3 py-2.5">
        {METHODS.map((m)=><option key={m.v} value={m.v}>{m.l}</option>)}
      </select>
    </div>
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Importe recibido</label>
      <input data-testid="admin-amount-input" type="number" step="0.01" value={form.amount} onChange={(e)=>setForm({...form,amount:e.target.value})} className="w-full bg-[var(--mep-surface)] border border-[var(--mep-border)] rounded-lg px-3 py-2.5" />
    </div>
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Moneda</label>
      <select data-testid="admin-currency-select" value={form.currency} onChange={(e)=>setForm({...form,currency:e.target.value})} className="w-full bg-[var(--mep-surface)] border border-[var(--mep-border)] rounded-lg px-3 py-2.5">
        {CURRENCIES.map((c)=><option key={c.v} value={c.v}>{c.l}</option>)}
      </select>
    </div>
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Referencia / Nota (opcional)</label>
      <input data-testid="admin-note-input" value={form.reference_note} onChange={(e)=>setForm({...form,reference_note:e.target.value})} className="w-full bg-[var(--mep-surface)] border border-[var(--mep-border)] rounded-lg px-3 py-2.5" />
    </div>
    <div className="sm:col-span-2">
      <button data-testid={testid} onClick={onSubmit} disabled={busy} className="btn-pink w-full py-3.5 font-semibold">{busy?"Procesando...":label}</button>
    </div>
  </div>
);
